import { useEffect, useMemo, useState } from "react";
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ladeKlimareihe, ladeWitterung } from "../dienste/witterung.js";
import { fmtZahl } from "../kern/format.js";
import { NV } from "../kern/konstanten.js";
import { Auswahl, Btn, Leer, TextKnopf } from "../ui/primitive.jsx";
import {
  aehnlicheJahrgaenge, fehlendesJahr, letztesAbgeschlossenesJahr, reiheVeraltet, reiheVorlaeufig,
} from "../dienste/jahrgangsvergleich.js";
import { stilprofil } from "../dienste/stilprofil.js";
import { ausKlimareihe, ersatzAusReihe } from "../dienste/wetterlauf.js";
import { locale, t } from "../i18n/index.js";

/** "2018-09-02" → „2. Sept.“ in der Sprache der Oberfläche. */
const tagMonat = (iso) => {
  const [j, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(j, m - 1, d)).toLocaleDateString(locale(), { day: "numeric", month: "short", timeZone: "UTC" });
};

/* `ausKlimareihe` (abgeschlossener Jahrgang aus der Reihe, ohne
   Abruf) und `ersatzAusReihe` liegen in dienste/wetterlauf.js — der
   Ladelauf im Menü braucht sie ebenso. */

const STUFE_TEXT = {
  sehr: "sehr ähnlich", gut: "gut vergleichbar",
  mittel: "in Teilen ähnlich", gering: "gering ähnlich",
};

/** Monat als Kurzname in der Sprache der Oberfläche (1 = Januar). */
const monatKurz = (m) => new Date(Date.UTC(2000, m - 1, 1))
  .toLocaleDateString(locale(), { month: "short", timeZone: "UTC" });

/* „1,2 °C wärmer" statt einer nackten Kennzahl. Unterhalb der
   Schwelle wird nichts gesagt — ein Zehntelgrad ist kein Merkmal. */
function beschreibeUnterschied(wert, schwelle, mehr, weniger, einheit) {
  if (wert == null || Math.abs(wert) < schwelle) return null;
  const betrag = Math.abs(wert);
  /* Zahl in der Sprache der Oberfläche — vorher stand auch im
     Englischen „0,4 °C cooler“. */
  const zahl = fmtZahl(betrag, einheit === " °C" ? 1 : 0);
  return `${zahl}${einheit} ${t(wert > 0 ? mehr : weniger)}`;
}


function WitterungsPanel({ region, db, setDb, vorwahlJahr }) {
  /* Südlich des Äquators läuft die Vegetationsperiode über den
     Jahreswechsel (Okt des Vorjahres bis Apr) — Beschriftungen und
     Frostmonate richten sich danach. */
  const sued = region && region.lat != null && region.lat < 0;
  const aktuellesJahr = new Date().getFullYear();
  /* Vorwahl: der jüngste abgeschlossene Jahrgang — im Norden ab dem
     6. November das laufende Kalenderjahr, nicht erst ab Neujahr. */
  const [jahr, setJahr] = useState(vorwahlJahr || letztesAbgeschlossenesJahr(sued));
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState(null);
  /* Jahresvergleich: Feld klappt erst auf Anforderung aus. */
  const [vglOffen, setVglOffen] = useState(false);
  const [vglJahr, setVglJahr] = useState(null);
  const [vglLaedt, setVglLaedt] = useState(false);

  useEffect(() => { if (vorwahlJahr) setJahr(vorwahlJahr); }, [vorwahlJahr]);

  const schluessel = `${region.id}:${jahr}`;
  const daten = db.weatherCache ? db.weatherCache[schluessel] : null;
  const vglSchluessel = vglJahr ? `${region.id}:${vglJahr}` : null;
  const vglDaten = vglSchluessel && db.weatherCache ? db.weatherCache[vglSchluessel] : null;
  const [klimaLaedt, setKlimaLaedt] = useState(false);

  const jahre = [];
  for (let j = aktuellesJahr; j >= 1950; j--) jahre.push(j);

  /* Klimareihe der Region: alle im Open-Meteo-Archiv verfügbaren
     Jahre, je Jahr auf die Vegetationsperiode verdichtet. */
  const klima = (db.klimaCache || {})[region.id] || null;

  /* Stilprofil des gewählten Jahres — wie der Vergleich allein aus
     der Klimareihe, also ohne weiteren Abruf. */
  const stil = useMemo(() => stilprofil(klima, Number(jahr)), [klima, jahr]);

  /* Abgeschlossen, aber noch nicht in der geladenen Reihe? Und ist
     der Reiferegen des gewählten Jahres nur hochgerechnet? */
  const fehlt = fehlendesJahr(klima);
  const vorlaeufig = reiheVorlaeufig(klima, Number(jahr));

  /* Die drei nächstgelegenen Jahrgänge zum gewählten Jahr. */
  const aehnliche = useMemo(
    () => aehnlicheJahrgaenge(klima, Number(jahr), 3),
    [klima, jahr]);

  /* Mittelwerte über die komplette Reihe sowie über die letzten
     10 Kalenderjahre. */
  const regionsWerte = useMemo(() => {
    /* `jahre` kann fehlen — bei einem älteren Klimacache oder einem
       abgebrochenen Abruf. Ohne diesen Riegel warf `reihen.slice`
       und riss die ganze Regionenansicht mit. */
    /* `di` liegt in `diJeJahr` neben der Reihe — für die Mittelwerte
       muss es am Jahr hängen, sonst bleibt die Spalte leer. */
    const reihen = (klima && Array.isArray(klima.jahre))
      ? klima.jahre.map((j) => (j.di != null || !klima.diJeJahr
          ? j : { ...j, di: klima.diJeJahr[j.jahr] }))
      : [];
    const mitteln = (liste) => {
      if (liste.length === 0) return null;
      const felder = ["tMittel", "tMax", "tMin", "regen", "sonne", "gdd", "hitzetage", "frosttage", "di"];
      const m = { anzahl: liste.length };
      for (const f of felder) {
        const werte = liste.map((e) => e[f]).filter((v) => v != null);
        m[f] = werte.length ? werte.reduce((summe, v) => summe + v, 0) / werte.length : null;
      }
      return m;
    };
    const zehn = klima && klima.letzteZehn ? new Set(klima.letzteZehn) : new Set(reihen.slice(-10).map((e) => e.jahr));
    return {
      alle: mitteln(reihen),
      letzte10: mitteln(reihen.filter((e) => zehn.has(e.jahr))),
    };
  }, [klima]);

  /* Jahrgangsmonate + dezente 10-Jahres-Referenz für das Diagramm */
  const chartDaten = useMemo(() => {
    if (!daten) return [];
    const referenz = klima && klima.monate10 ? klima.monate10 : null;
    return daten.monate.map((m) => {
      const ref = referenz ? referenz.find((x) => x.monat === m.monat) : null;
      const v = vglDaten ? vglDaten.monate.find((x) => x.monat === m.monat) : null;
      return {
        ...m,
        temp10: ref ? ref.temp : null, regen10: ref ? ref.regen : null,
        tempVgl: v ? v.temp : null, regenVgl: v ? v.regen : null,
      };
    });
  }, [daten, klima, vglDaten]);

  const klimaLaden = async () => {
    if (klimaLaedt) return;
    setFehler(null);
    setKlimaLaedt(true);
    try {
      const reihe = await ladeKlimareihe(region);
      setDb((prev) => {
        /* Die aus der alten Reihe abgeleiteten Jahrgänge müssen weg —
           sonst bleiben sie mit ihren damaligen Feldern liegen, und
           „Klimareihe aktualisieren“ wirkt scheinbar nicht. Genau so
           blieb der Trockenheitsindex leer.

           Nur die abgeleiteten (`ausKlimareihe`): Ein direkt
           abgerufener Jahrgang — das laufende Jahr — ist eigene
           Arbeit und bleibt stehen. */
        const behalten = {};
        for (const [k, v] of Object.entries(prev.weatherCache || {})) {
          if (k.startsWith(`${region.id}:`) && v && v.ausKlimareihe) continue;
          behalten[k] = v;
        }
        return {
          ...prev,
          weatherCache: behalten,
          klimaCache: { ...(prev.klimaCache || {}), [region.id]: reihe },
        };
      });
    } catch (e) {
      setFehler(`Die Klimareihe konnte nicht geladen werden: ${(e && e.message) || "unbekannter Fehler"}. Bitte Netzwerkverbindung prüfen und erneut versuchen.`);
    } finally {
      setKlimaLaedt(false);
    }
  };

  const abrufenFuer = async (j, setzeLaden) => {
    setzeLaden(true); setFehler(null);
    try {
      /* Liegt die Klimareihe schon vor, steht das Jahr längst darin —
         sie trägt seit dem Umbau in wetter.js den Monatsverlauf jedes
         abgeschlossenen Jahres mit. Dann wird gar nichts geholt, auch
         nicht vom eigenen Server: Der Jahrgang entsteht hier aus dem,
         was ohnehin im Kellerbuch liegt. Das laufende Jahr fehlt in
         der Reihe und geht weiterhin den normalen Weg. */
      const ausReihe = ausKlimareihe(klima, region, j);
      if (ausReihe) {
        setDb((prev) => ({ ...prev, weatherCache: { ...(prev.weatherCache || {}), [`${region.id}:${j}`]: ausReihe } }));
        return;
      }
      const w = await ladeWitterung(region, j);
      setDb((prev) => ({ ...prev, weatherCache: { ...(prev.weatherCache || {}), [`${region.id}:${j}`]: w } }));
    } catch (e) {
      setFehler(
        ((e && e.message) || "Der Abruf ist fehlgeschlagen.") +
        " Bitte Netzwerkverbindung prüfen und erneut versuchen."
      );
    } finally {
      setzeLaden(false);
    }
  };
  const abrufen = () => abrufenFuer(jahr, setLaden);

  /* Steht das Jahr in der geladenen Klimareihe, kostet seine
     Aufbereitung nichts — kein Netz, kein Warten. Dann gibt es auch
     keinen Grund, erst zu fragen: Es wird beim Wechsel des Jahres
     einfach hingelegt.

     Nur für die laufende Periode bleibt der Knopf nötig; sie fehlt in
     der Reihe und muss abgerufen werden. Genau dort ist die Frage
     auch berechtigt, weil sie Zeit kostet. */
  useEffect(() => {
    /* Ein vorhandener Eintrag reicht nicht: Stammt er aus einer
       früheren Fassung — etwa vor dem Trockenheitsindex —, fehlen
       Felder, die die Reihe längst hergibt. Und ein direkt abgerufener
       Jahrgang der damals laufenden Saison endet am Tag des Abrufs;
       steht das Jahr inzwischen abgeschlossen in der Reihe, wird er
       ersetzt (`ersatzAusReihe`). Bis November 2026 blieb er stehen. */
    const fertig = ersatzAusReihe(daten, klima, region, Number(jahr));
    if (!fertig) return;
    setDb((prev) => ({ ...prev, weatherCache: { ...(prev.weatherCache || {}), [schluessel]: fertig } }));
  }, [klima, jahr, region.id, daten, schluessel]);

  /* Dasselbe für das Vergleichsjahr. */
  useEffect(() => {
    if (!vglJahr || vglDaten) return;
    const fertig = ausKlimareihe(klima, region, Number(vglJahr));
    if (!fertig) return;
    setDb((prev) => (prev.weatherCache && prev.weatherCache[vglSchluessel]
      ? prev
      : { ...prev, weatherCache: { ...(prev.weatherCache || {}), [vglSchluessel]: fertig } }));
  }, [klima, vglJahr, region.id, vglDaten, vglSchluessel]);

  return (
    <section className="wl-abschnitt">
      <div className="wl-abschnittskopf">
        <h2 className="wl-abschnittstitel">{t("Witterungsprofil des Jahrgangs")}</h2>
        <div className="wl-witterungssteuerung">
          <Auswahl value={jahr} onChange={(e) => setJahr(parseInt(e.target.value, 10))} aria-label={t("Jahrgang wählen")}>
            {jahre.filter((j) => j !== NV).map((j) => <option key={j} value={j}>{j}</option>)}
          </Auswahl>
          {/* Der Knopf verschwindet, wenn er nichts mehr zu tun hat:
              Stammt das Jahr aus der Klimareihe, ist es bereits da und
              „Neu abrufen“ verspräche etwas, das es nicht gibt. Sichtbar
              bleibt er für das laufende Jahr und wenn die Reihe fehlt. */}
          {!(daten && daten.ausKlimareihe) && (
            <Btn variante="voll" onClick={abrufen} disabled={laden || klimaLaedt}>
              {t(laden ? "Wird geladen …" : daten ? "Neu abrufen" : "Witterung abrufen")}
            </Btn>
          )}
          <Btn onClick={klimaLaden} disabled={klimaLaedt}
            title={t("Lädt alle verfügbaren Jahre des Open-Meteo-Archivs in einem Abruf und berechnet die Mittelwerte.")}>
            {t(klimaLaedt
              ? "Klimareihe wird geladen …"
              : klima
                ? "Klimareihe aktualisieren"
                : "Klimareihe laden")}
          </Btn>
        </div>
      </div>

      <div className="wl-vergleichsleiste">
        {!vglOffen ? (
          <TextKnopf onClick={() => { setVglOffen(true); if (!vglJahr) setVglJahr(jahr - 1); }}>{t("Mit anderem Jahr vergleichen")}</TextKnopf>
        ) : (
          <div className="wl-vergleichsfeld">
            <span className="wl-feldname">{t("Vergleichsjahr")}</span>
            <Auswahl value={vglJahr || ""} onChange={(e) => setVglJahr(parseInt(e.target.value, 10))} aria-label={t("Vergleichsjahr wählen")}>
              {jahre.filter((j) => j !== NV && j !== jahr).map((j) => <option key={j} value={j}>{j}</option>)}
            </Auswahl>
            <Btn onClick={() => abrufenFuer(vglJahr, setVglLaedt)} disabled={vglLaedt || !vglJahr}>
              {t(vglLaedt ? "Wird geladen …" : vglDaten ? "Neu abrufen" : "Abrufen")}
            </Btn>
            <TextKnopf className="ist-dezent" onClick={() => { setVglOffen(false); setVglJahr(null); }}>{t("Beenden")}</TextKnopf>
          </div>
        )}
      </div>

      {fehler && <p className="wl-fehler">{fehler}</p>}

      {/* Die Reihe liegt ohne Verfallsdatum im Kellerbuch: Wurde sie vor
          Saisonende geladen, fehlt ihr das jüngste Jahr — Vergleich und
          Stilprofil gäbe es dafür erst nach einem Klick, und niemand
          sagte es. */}
      {fehlt != null && !klimaLaedt && (
        <p className="wl-hinweis wl-witterungshinweis">
          {t("Jahrgang {jahr} ist abgeschlossen, fehlt aber noch in der geladenen Klimareihe — „Klimareihe aktualisieren“ nimmt ihn in Vergleich und Stilprofil auf.", { jahr: fehlt })}
        </p>
      )}

      {!daten && !fehler && !laden && (
        <Leer text={`Vegetationsperiode ${jahr} (${sued ? "01.10. des Vorjahres – 30.04." : "01.04.–31.10."}) für ${region.name} abrufen — Tagesdaten werden zu Kennzahlen und Monatsverlauf verdichtet.`} />
      )}

      {daten && (
        <>
          <div className="wl-diagramm">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={chartDaten} margin={{ top: 8, right: 4, bottom: 0, left: -8 }}>
                <CartesianGrid stroke="#E8E4D9" vertical={false} />
                <XAxis dataKey="monat" tick={{ fontSize: 11, fill: "#6F6A5E" }} axisLine={{ stroke: "#D8D3C4" }} tickLine={false} />
                <YAxis yAxisId="regen" tick={{ fontSize: 11, fill: "#6F6A5E" }} axisLine={false} tickLine={false} width={44} />
                <YAxis yAxisId="temp" orientation="right" tick={{ fontSize: 11, fill: "#722F37" }} axisLine={false} tickLine={false} width={38} />
                {/* Kantig statt gerundet und in Lesegröße: Auf dem
                    Telefon war der Kasten mit 12 px und weichen Ecken
                    kaum zu entziffern. */}
                <Tooltip
                  contentStyle={{ background: "#FBFAF6", border: "1px solid #A9A392", borderRadius: 0,
                    fontSize: 13, lineHeight: 1.5, padding: "8px 10px", color: "#1B1914" }}
                  labelStyle={{ fontSize: 12, color: "#6F6A5E", marginBottom: 4 }}
                  itemStyle={{ padding: 0 }}
                  formatter={(wert, name) => String(name).includes("Niederschlag") ? [`${wert} mm`, name] : [`${wert} °C`, name]}
                />
                <Bar yAxisId="regen" dataKey="regen" name="Niederschlag" fill="#B9C0BA" barSize={22} />
                {klima && klima.monate10 && (
                  <Line yAxisId="regen" dataKey="regen10" name="Ø Niederschlag · 10 J." stroke="#8E978F"
                    strokeWidth={1.1} strokeDasharray="4 4" strokeOpacity={0.65} dot={false} />
                )}
                {klima && klima.monate10 && (
                  <Line yAxisId="temp" dataKey="temp10" name="Ø Temperatur · 10 J." stroke="#722F37"
                    strokeWidth={1.1} strokeDasharray="4 4" strokeOpacity={0.38} dot={false} />
                )}
                {vglDaten && (
                  <Line yAxisId="regen" dataKey="regenVgl" name={`Niederschlag · ${vglJahr}`} stroke="#6F7F8E"
                    strokeWidth={1.2} strokeDasharray="2 3" dot={false} />
                )}
                {vglDaten && (
                  <Line yAxisId="temp" dataKey="tempVgl" name={`Ø Temperatur · ${vglJahr}`} stroke="#B9A15C"
                    strokeWidth={1.6} dot={false} />
                )}
                <Line yAxisId="temp" dataKey="temp" name="Ø Temperatur" stroke="#722F37" strokeWidth={1.6} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <p className="wl-quelle">
              Zeitraum {daten.zeitraum} · Linie: Ø-Temperatur (°C, rechts) · Balken: Niederschlag (mm, links)
              {klima && klima.monate10 ? ` · gestrichelt: Ø letzte 10 Jahre (${klima.zeitraum10})` : ""} · Quelle: Open-Meteo Historical Weather API
            </p>
          </div>
        </>
      )}

      {/* Jahrgänge mit ähnlicher Witterung — die Überschrift verspricht
          nicht mehr, als belegt ist: ähnliches Wetter, nicht zwingend
          ähnlicher Wein (siehe jahrgangsvergleich.js). Braucht nur die
          Klimareihe, keinen
          weiteren Abruf: Sie trägt die Kennzahlen jedes Jahres seit
          1950 bereits in sich. */}
      {stil && (
        <div className="wl-stil">
          <h3 className="wl-abschnittstitel">{t("Stilprofil {jahr}", { jahr: stil.jahr })}</h3>
          <ul className="wl-stil-achsen">
            {stil.achsen.filter((a) => a.stufe != null).map((a) => (
              <li key={a.schluessel} className={"wl-stil-achse ist-" + a.stufe}>
                <span className="wl-stil-label">{t(a.label)}</span>
                {/* Rangplatz als Balken: links die niedrigsten Jahre der
                    Region, rechts die höchsten. */}
                <span className="wl-stil-skala" aria-hidden="true">
                  <span className="wl-stil-marke" style={{ "--lage": a.lage ?? 0.5 }} />
                </span>
                <span className="wl-stil-wort">{t(a.wort)}</span>
              </li>
            ))}
          </ul>
          <ul className="wl-stil-merkmale">
            {stil.merkmale.map((m) => <li key={m}>{t(m)}</li>)}
          </ul>
          {stil.lese && (
            /* Regen, Nächte und Bodenwasser sind an diesem Termin
               gemessen — ohne ihn wäre „Regen in der Reife“ nicht
               nachvollziehbar. */
            <p className="wl-stil-lese">
              {t("Reife geschätzt: {datum}", { datum: tagMonat(stil.lese) })}
              {stil.leseMittel && ` · ${t("im Mittel der Region: {datum}", { datum: tagMonat(`2000-${stil.leseMittel}`) })}`}
            </p>
          )}
          {vorlaeufig && (
            <p className="wl-hinweis wl-witterungshinweis">
              {Date.now() >= vorlaeufig.abMs
                ? t("Reiferegen noch hochgerechnet — die Tage nach der Reife liegen jetzt im Archiv. „Klimareihe aktualisieren“ ersetzt die Schätzung.")
                : t("Reiferegen noch hochgerechnet — die Tage nach der Reife fehlen im Archiv. Ab {datum} „Klimareihe aktualisieren“.", { datum: tagMonat(vorlaeufig.ab) })}
            </p>
          )}
        </div>
      )}

      {aehnliche.length > 0 && (
        <div className="wl-aehnlich">
          <h3 className="wl-abschnittstitel">
            {t("Jahrgänge mit ähnlicher Witterung in {region}", { region: region.name })}
          </h3>
          <ul className="wl-aehnlich-liste">
            {aehnliche.map((a) => (
              <li key={a.jahr} className={"wl-aehnlich-eintrag ist-" + (a.stufe || "gering")}>
                <span className="wl-aehnlich-jahr num">{a.jahr}</span>
                <span className="wl-aehnlich-stufe">{t(STUFE_TEXT[a.stufe] || "gering ähnlich")}</span>
                <span className="wl-aehnlich-details">
                  {[
                    beschreibeUnterschied(a.unterschiede.tMittel, 0.15, "wärmer", "kühler", " °C"),
                    /* Der Reifeabstand zuerst nach der Wärme: Er sagt,
                       WANN der Vergleichsjahrgang reif war. Regen und
                       Sonne bis zur Reife je Tag, auf die übliche Dauer
                       hochgerechnet (ab Klimareihe Fassung 5) — die
                       Summen bis zur Reife nannten ein spätes, trübes
                       Jahr „sonniger“, nur weil es länger dauerte. Ältere
                       Reihen: Saisonsummen, die haben dieses Problem nicht. */
                    beschreibeUnterschied(a.unterschiede.leseTag, 5, "später reif", "früher reif", " " + t("Tage")),
                    beschreibeUnterschied(a.unterschiede.regenBisReife ?? a.unterschiede.regen, 8, "nasser", "trockener", " mm"),
                    beschreibeUnterschied(a.unterschiede.sonneBisReife ?? a.unterschiede.sonne, 25, "sonniger", "trüber", " h"),
                  ].filter(Boolean).join(" · ") || t("nahezu deckungsgleich")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="wl-mittelwerte">
        <div className="wl-mittelkopf">
          <h3 className="wl-mitteltitel">{t("Kennzahlen im Vergleich")}</h3>
        </div>
        {!daten && !klima ? (
          <p className="wl-hinweis wl-mittelhinweis">
            Noch nichts geladen — „Klimareihe laden" holt alle Jahrgänge der Region auf einmal; danach steht jeder abgeschlossene Jahrgang ohne weiteren Abruf bereit.
          </p>
        ) : (
          <div className="wl-scroll">
            <table className="wl-tabelle wl-tabelle--kompakt">
              <thead>
                <tr>
                  <th>{t("Kennzahl")}</th>
                  <th>{jahr}</th>
                  {vglJahr && <th>{vglJahr}</th>}
                  {vglJahr && <th>{t("Differenz")}</th>}
                  <th>{t("Ø letzte 10 Jahre")}</th>
                  <th>{t("Ø alle Jahre")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [t("Ø Temperatur"), (m) => m.tMittel != null ? `${fmtZahl(m.tMittel, 1)} °C` : "—", "tMittel", 1],
                  [t("Ø Höchsttemperatur"), (m) => m.tMax != null ? `${fmtZahl(m.tMax, 1)} °C` : "—", "tMax", 1],
                  [t("Ø Tiefsttemperatur"), (m) => m.tMin != null ? `${fmtZahl(m.tMin, 1)} °C` : "—", "tMin", 1],
                  [t("Niederschlag"), (m) => m.regen != null ? `${fmtZahl(m.regen, 0)} mm` : "—", "regen", 0],
                  [t("Sonnenstunden"), (m) => m.sonne != null ? `${fmtZahl(m.sonne, 0)} h` : "—", "sonne", 0],
                  [t("Gradtage (Basis 10 °C)"), (m) => m.gdd != null ? fmtZahl(m.gdd, 0) : "—", "gdd", 0],
                  [t("Hitzetage ≥ 30 °C"), (m) => m.hitzetage != null ? fmtZahl(m.hitzetage, 1) : "—", "hitzetage", 1],
                  [`${t("Frosttage")} ${sued ? `${monatKurz(10)}–${monatKurz(11)}` : `${monatKurz(4)}–${monatKurz(5)}`}`, (m) => m.frosttage != null ? fmtZahl(m.frosttage, 1) : "—", "frosttage", 1],
                  /* Trockenheitsindex nach Riou: Bodenwasser am Ende
                     der Periode. Nicht dasselbe wie die Regensumme —
                     2006 hatte 430 mm Regen und trotzdem 0 mm übrig,
                     weil die Hitze ihn verdunstet hat. */
                  [t("Bodenwasser Periodenende"), (m) => m.di != null ? `${fmtZahl(m.di, 0)} mm` : "—", "di", 0],
                ].map(([bezeichnung, format, feld, stellen]) => (
                  <tr key={feld}>
                    <td>{bezeichnung}</td>
                    <td className="num">{daten ? format(daten) : "—"}</td>
                    {vglJahr && <td className="num ist-vergleich">{vglDaten ? format(vglDaten) : "—"}</td>}
                    {vglJahr && (
                      <td className="num">
                        {daten && vglDaten && daten[feld] != null && vglDaten[feld] != null
                          ? (() => {
                              const d = daten[feld] - vglDaten[feld];
                              const vz = d > 0 ? "+" : "";
                              return <span className={d > 0 ? "wl-diff-plus" : d < 0 ? "wl-diff-minus" : ""}>{vz}{fmtZahl(d, stellen)}</span>;
                            })()
                          : "—"}
                      </td>
                    )}
                    <td className="num">{regionsWerte.letzte10 ? format(regionsWerte.letzte10) : "—"}</td>
                    <td className="num">{regionsWerte.alle ? format(regionsWerte.alle) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Die Reihe liegt ohne Verfallsdatum im Kellerbuch. Ist sie
            älter als die Rechnung, laufen Vergleich und Stilprofil auf
            der älteren Fassung weiter — richtig, aber nicht auf dem
            neuen Stand. */}
        {reiheVeraltet(klima) && fehlt == null && (
          <p className="wl-hinweis wl-mittelhinweis">
            {t("Diese Klimareihe stammt aus einer älteren Fassung — einmal „Klimareihe aktualisieren“, damit Vergleich und Stilprofil neu gerechnet werden.")}
          </p>
        )}
      </div>
    </section>
  );
}

export { WitterungsPanel };
